'use client';

import clsx from 'clsx';
import Image from 'next/image';
import type { PropsWithChildren, ReactNode } from 'react';
import { Sheet } from 'react-modal-sheet';
import styles from '@/app/cc/_component/ui/common/BottomSheet/BottomSheet.module.scss';

interface BottomSheetProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly header?: ReactNode;
  readonly cta?: ReactNode;
  readonly disableScrollLocking?: boolean;
  readonly children: ReactNode;
  readonly className?: string;
}

function BottomSheet({
  isOpen,
  onClose,
  header,
  cta,
  disableScrollLocking = false,
  children,
  className,
}: BottomSheetProps) {
  return (
    <Sheet
      isOpen={isOpen}
      onClose={onClose}
      detent="content"
      className={clsx(styles.sheet, className)}
      disableScrollLocking={disableScrollLocking}
    >
      <Sheet.Backdrop onTap={onClose} />
      <Sheet.Container className={styles.container}>
        <Sheet.Content className={styles.content} disableScroll scrollClassName={styles.content_scroller}>
          {header}
          <div className={styles.body}>{children}</div>
          {cta}
        </Sheet.Content>
      </Sheet.Container>
    </Sheet>
  );
}

interface HeaderProps {
  readonly className?: string;
  readonly onClose: () => void;
}

function Header({ children, className, onClose }: PropsWithChildren<HeaderProps>) {
  return (
    <div className={clsx(styles.header, className)}>
      <h3 className={styles.title}>{children}</h3>
      <button type="button" className={styles.closeButton} onClick={onClose}>
        <Image src="/cc/image/icon_X.svg" alt="close" width={48} height={48} />
      </button>
    </div>
  );
}

interface CTAProps {
  readonly direction?: 'column' | 'row';
  readonly layout?: 'full' | 'hug';
  readonly children: ReactNode;
  readonly className?: string;
}

function CTA({ direction = 'column', layout = 'full', children, className }: CTAProps) {
  return (
    <div className={clsx(styles.cta, className)}>
      <div className={clsx(styles.cta_wrap, styles[direction], styles[layout])}>{children}</div>
    </div>
  );
}

BottomSheet.Header = Header;
BottomSheet.CTA = CTA;

export default BottomSheet;
